<template>
  <div class="sc-page">
    <!-- Loading -->
    <div
      v-if="loadingData"
      class="sc-loading"
    >
      <div class="sc-loading__spinner" />
      <p class="mb-0 text-muted">
        Loading contact…
      </p>
    </div>

    <template v-else>
      <!-- Hero -->
      <section class="sc-hero">
        <div class="sc-hero__glow" />
        <div class="d-flex align-items-center justify-content-between flex-wrap gap-12">
          <div class="d-flex align-items-center">
            <div
              class="sc-hero__avatar"
              :style="{ background: avatarColor }"
            >
              {{ initials(delegator.name) }}
            </div>
            <div class="ml-1">
              <h2 class="mb-0 sc-hero__title">
                {{ delegator.name || 'Contact' }}
              </h2>
              <p class="mb-0 sc-hero__subtitle">
                <feather-icon
                  icon="HashIcon"
                  size="13"
                />
                {{ $route.params.id }} · Contact overview
              </p>
            </div>
          </div>
          <div class="sc-hero__actions">
            <button
              type="button"
              class="sc-btn sc-btn--success"
              title="Create a CRM opportunity for this customer"
              @click="showOppModal = true"
            >
              <feather-icon
                icon="TargetIcon"
                size="16"
              />
              New Opportunity
            </button>
            <button
              type="button"
              class="sc-btn sc-btn--primary"
              @click="goEdit"
            >
              <feather-icon
                icon="Edit2Icon"
                size="16"
              />
              Edit
            </button>
            <button
              type="button"
              class="sc-btn sc-btn--ghost"
              @click="goBack"
            >
              <feather-icon
                icon="ArrowLeftIcon"
                size="16"
              />
              Back
            </button>
          </div>
        </div>
      </section>

      <div class="sc-grid">
        <!--
          LIVE FROM THE ERP — Karim, 2 Sep.
          Facilities this person acts for, their applications, and the next
          certificate to expire. None of it is stored; it is read at open time, so a
          request logged an hour ago is here.
        -->
        <section
          v-if="crm.nearestCertificate || crm.requestSummary.total"
          class="sc-card sc-card--span2"
        >
          <header class="sc-card__head">
            <span class="sc-card__head-icon sc-card__head-icon--primary">
              <feather-icon
                icon="ActivityIcon"
                size="18"
              />
            </span>
            <div>
              <h3 class="sc-card__title">
                Activity
              </h3>
              <p class="sc-card__subtitle">
                Live from the ERP — applications and certificates.
              </p>
            </div>
          </header>

          <div class="sc-card__body">
            <div class="sc-act">
              <div class="sc-act__item">
                <span class="sc-act__label">Next certificate to expire</span>
                <span
                  v-if="crm.nearestCertificate"
                  class="sc-act__value"
                >
                  {{ crm.nearestCertificate.name || crm.nearestCertificate.number || 'Certificate' }}
                  <small>
                    — {{ crm.nearestCertificate.expiresAt }}
                    ({{ crm.nearestCertificate.daysToExpiry }} days)
                  </small>
                </span>
                <span
                  v-else
                  class="sc-act__value sc-act__value--muted"
                >No certificate expiring ahead</span>
              </div>

              <div class="sc-act__item">
                <span class="sc-act__label">Last application</span>
                <span
                  v-if="crm.requests.length"
                  class="sc-act__value"
                >
                  {{ crm.requests[0].code || ('#' + crm.requests[0].id) }}
                  <small>— {{ crm.requests[0].certificate || 'unknown type' }},
                    {{ (crm.requests[0].createdAt || '').slice(0, 10) }}</small>
                </span>
                <span
                  v-else
                  class="sc-act__value sc-act__value--muted"
                >No applications yet</span>
              </div>

              <div class="sc-act__item">
                <span class="sc-act__label">Applications</span>
                <span class="sc-act__value">
                  {{ crm.requestSummary.total || 0 }} total
                  <small v-if="crm.requestSummary.open">
                    — {{ crm.requestSummary.open }} still open
                  </small>
                </span>
              </div>
            </div>

            <!-- The many-to-many the CRM could not express: one client, many
                 facilities. Four or more makes them a broker. -->
            <div
              v-if="crm.facilities.length"
              class="mt-2"
            >
              <h4 class="sc-act__heading">
                Facilities ({{ crm.facilities.length }})
                <span
                  v-if="crm.facilities.length > 3"
                  class="sc-act__broker"
                >broker</span>
              </h4>
              <table class="sc-mini-table">
                <thead>
                  <tr><th>Facility</th><th>CR</th><th class="text-right">
                    Requests
                  </th><th>Last request</th></tr>
                </thead>
                <tbody>
                  <tr
                    v-for="f in crm.facilities"
                    :key="f.facilityId"
                  >
                    <td>
                      <a
                        href="#"
                        @click.prevent="goToCompany(f.facilityId)"
                      >
                        {{ f.name || ('Facility #' + f.facilityId) }}
                      </a>
                    </td>
                    <td>{{ f.crNumber || '—' }}</td>
                    <td class="text-right">
                      {{ f.requests }}
                    </td>
                    <td>{{ (f.lastRequestAt || '').slice(0, 10) || '—' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div
              v-if="crm.requests.length"
              class="mt-2"
            >
              <h4 class="sc-act__heading">
                Recent applications
              </h4>
              <table class="sc-mini-table">
                <thead>
                  <tr><th>Request</th><th>Certificate</th><th>Source</th><th>Status</th><th>Date</th></tr>
                </thead>
                <tbody>
                  <tr
                    v-for="r in crm.requests"
                    :key="r.id"
                  >
                    <td>{{ r.code || ('#' + r.id) }}</td>
                    <td>{{ r.certificate || '—' }}</td>
                    <td>{{ r.source || '—' }}</td>
                    <td>
                      <span :class="r.open ? 'sc-pill sc-pill--open' : 'sc-pill'">
                        {{ r.status || (r.open ? 'open' : 'closed') }}
                      </span>
                    </td>
                    <td>{{ (r.createdAt || '').slice(0, 10) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <!--
          Karim, 3 Sep: "if I opened a client, I can see the open
          opportunities for it... and I can open multiple opportunities."
          showClient() constrains crm.cards to open (not won/rejected/
          renewed/dismissed) — a client's live pipeline, not his history.
        -->
        <section
          v-if="crm.cards.length"
          class="sc-card sc-card--span2"
        >
          <header class="sc-card__head">
            <span class="sc-card__head-icon sc-card__head-icon--success">
              <feather-icon
                icon="TargetIcon"
                size="18"
              />
            </span>
            <div>
              <h3 class="sc-card__title">
                Open Opportunities
                <span class="sc-card__count">{{ crm.cards.length }}</span>
              </h3>
              <p class="sc-card__subtitle">
                Currently in the pipeline for this contact.
              </p>
            </div>
          </header>

          <div class="sc-card__body">
            <table class="sc-mini-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Pipeline</th>
                  <th>Stage</th>
                  <th class="text-right">
                    Value
                  </th>
                  <th />
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="card in crm.cards"
                  :key="card.id"
                >
                  <td>{{ card.title || card.id }}</td>
                  <td>{{ pipelineLabel(card.pipeline) }}</td>
                  <td>{{ card.stage || '—' }}</td>
                  <td class="text-right">
                    {{ card.currency || 'AED' }} {{ formatNumber(card.value) }}
                  </td>
                  <td>
                    <router-link
                      :to="{ name: 'crm-system-pipeline', query: { clientId: $route.params.id, clientName: delegator.name, pipeline: card.pipeline } }"
                      class="sc-opp-link"
                    >
                      Open
                      <feather-icon
                        icon="ArrowRightIcon"
                        size="12"
                      />
                    </router-link>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <!-- Contact details -->
        <section class="sc-card sc-card--span2">
          <header class="sc-card__head">
            <span class="sc-card__head-icon sc-card__head-icon--primary">
              <feather-icon
                icon="UserIcon"
                size="18"
              />
            </span>
            <div>
              <h3 class="sc-card__title">
                Contact Details
              </h3>
              <p class="sc-card__subtitle">
                Primary information about this contact.
              </p>
            </div>
          </header>

          <div class="sc-card__body">
            <div class="sc-info-grid">
              <div class="sc-info">
                <span class="sc-info__icon sc-info__icon--primary">
                  <feather-icon
                    icon="UserIcon"
                    size="15"
                  />
                </span>
                <div class="sc-info__content">
                  <span class="sc-info__label">Name</span>
                  <span class="sc-info__value">{{ delegator.name || '—' }}</span>
                </div>
              </div>

              <!-- Karim, 2 Sep: "the company he is related to is not showing." The
                   client detail already eager-loads this (CrmClientController::
                   showClient with(['company', ...])) but nothing rendered it — the
                   only company reference on this page was the broker facilities
                   table below, which is empty for a normal single-facility contact. -->
              <a
                v-if="delegator.company"
                class="sc-info"
                href="#"
                @click.prevent="goToCompany(delegator.company.erp_facility_id)"
              >
                <span class="sc-info__icon sc-info__icon--accent">
                  <feather-icon
                    icon="HomeIcon"
                    size="15"
                  />
                </span>
                <div class="sc-info__content">
                  <span class="sc-info__label">Company</span>
                  <span class="sc-info__value">{{ delegator.company.name || ('Facility #' + delegator.company.erp_facility_id) }}</span>
                </div>
              </a>
              <div
                v-else
                class="sc-info sc-info--plain"
              >
                <span class="sc-info__icon sc-info__icon--accent">
                  <feather-icon
                    icon="HomeIcon"
                    size="15"
                  />
                </span>
                <div class="sc-info__content">
                  <span class="sc-info__label">Company</span>
                  <span class="sc-info__value">No facility on record</span>
                </div>
              </div>

              <a
                class="sc-info"
                :class="{ 'sc-info--plain': !delegator.email }"
                :href="delegator.email ? `mailto:${delegator.email}` : null"
              >
                <span class="sc-info__icon sc-info__icon--info">
                  <feather-icon
                    icon="MailIcon"
                    size="15"
                  />
                </span>
                <div class="sc-info__content">
                  <span class="sc-info__label">Email</span>
                  <span class="sc-info__value">{{ delegator.email || '—' }}</span>
                </div>
              </a>

              <a
                class="sc-info"
                :class="{ 'sc-info--plain': !delegator.phone }"
                :href="delegator.phone ? `tel:${delegator.phone}` : null"
              >
                <span class="sc-info__icon sc-info__icon--success">
                  <feather-icon
                    icon="PhoneIcon"
                    size="15"
                  />
                </span>
                <div class="sc-info__content">
                  <span class="sc-info__label">Phone</span>
                  <span class="sc-info__value">{{ delegator.phone || '—' }}</span>
                </div>
              </a>

              <div class="sc-info sc-info--plain">
                <span class="sc-info__icon sc-info__icon--accent">
                  <feather-icon
                    icon="BriefcaseIcon"
                    size="15"
                  />
                </span>
                <div class="sc-info__content">
                  <span class="sc-info__label">Broker</span>
                  <span
                    class="sc-badge"
                    :class="isBroker ? 'sc-badge--success' : 'sc-badge--muted'"
                  >
                    <feather-icon
                      :icon="isBroker ? 'CheckCircleIcon' : 'XCircleIcon'"
                      size="12"
                    />
                    {{ isBroker ? 'Yes' : 'No' }}
                  </span>
                </div>
              </div>

              <!-- Karim, 2 Sep: "add client rating" — a judgement call the owner
                   or a manager sets, not something computed. -->
              <div class="sc-info sc-info--plain">
                <span
                  class="sc-info__icon"
                  :class="`sc-info__icon--tier-${delegator.tier || 'none'}`"
                >
                  <feather-icon
                    icon="AwardIcon"
                    size="15"
                  />
                </span>
                <div class="sc-info__content">
                  <span class="sc-info__label">Rating</span>
                  <select
                    class="sc-tier-select"
                    :class="`sc-tier-select--${delegator.tier || 'none'}`"
                    :value="delegator.tier || ''"
                    :disabled="savingTier"
                    @change="setTier($event.target.value)"
                  >
                    <option value="">
                      Not rated
                    </option>
                    <option value="gold">
                      Gold
                    </option>
                    <option value="silver">
                      Silver
                    </option>
                    <option value="bronze">
                      Bronze
                    </option>
                  </select>
                </div>
              </div>

              <div
                v-if="delegator.wallet_balance"
                class="sc-info"
              >
                <span class="sc-info__icon sc-info__icon--warning">
                  <feather-icon
                    icon="CreditCardIcon"
                    size="15"
                  />
                </span>
                <div class="sc-info__content">
                  <span class="sc-info__label">Wallet Balance</span>
                  <span class="sc-info__value">{{ delegator.wallet_balance }}</span>
                </div>
              </div>

              <div
                v-if="delegator.offer"
                class="sc-info"
              >
                <span class="sc-info__icon sc-info__icon--accent">
                  <feather-icon
                    icon="TagIcon"
                    size="15"
                  />
                </span>
                <div class="sc-info__content">
                  <span class="sc-info__label">Offer</span>
                  <span class="sc-info__value">{{ delegator.offer.name }} — {{ delegator.offer.amount }}%</span>
                </div>
              </div>

              <!-- <div class="sc-info">
                <span class="sc-info__icon sc-info__icon--info">
                  <feather-icon
                    icon="ZapIcon"
                    size="15"
                  />
                </span>
                <div class="sc-info__content">
                  <span class="sc-info__label">Auto Proceed</span>
                  <span
                    class="sc-badge"
                    :class="delegator.auto_proceed === 1 ? 'sc-badge--success' : 'sc-badge--muted'"
                  >
                    <feather-icon
                      :icon="delegator.auto_proceed === 1 ? 'CheckCircleIcon' : 'XCircleIcon'"
                      size="12"
                    />
                    {{ delegator.auto_proceed === 1 ? 'Enabled' : 'Disabled' }}
                  </span>
                </div>
              </div> -->
            </div>
          </div>
        </section>

        <!-- Assets -->
        <section
          v-if="delegator.signature || delegator.stamp"
          class="sc-card sc-card--span2"
        >
          <header class="sc-card__head">
            <span class="sc-card__head-icon sc-card__head-icon--info">
              <feather-icon
                icon="ImageIcon"
                size="18"
              />
            </span>
            <div>
              <h3 class="sc-card__title">
                Assets
              </h3>
              <p class="sc-card__subtitle">
                Signature &amp; stamp on file.
              </p>
            </div>
          </header>

          <div class="sc-card__body">
            <div class="sc-asset-grid">
              <div class="sc-asset">
                <span class="sc-asset__label">
                  <feather-icon
                    icon="Edit3Icon"
                    size="13"
                  />
                  Signature
                </span>
                <div class="sc-asset__box">
                  <img
                    v-if="delegator.signature"
                    :src="delegator.signature"
                    alt="signature"
                  >
                  <span
                    v-else
                    class="sc-asset__empty"
                  >None</span>
                </div>
              </div>
              <div class="sc-asset">
                <span class="sc-asset__label">
                  <feather-icon
                    icon="AwardIcon"
                    size="13"
                  />
                  Stamp
                </span>
                <div class="sc-asset__box">
                  <img
                    v-if="delegator.stamp"
                    :src="delegator.stamp"
                    alt="stamp"
                  >
                  <span
                    v-else
                    class="sc-asset__empty"
                  >None</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Certificates -->
        <section class="sc-card sc-card--span2">
          <header class="sc-card__head">
            <span class="sc-card__head-icon sc-card__head-icon--success">
              <feather-icon
                icon="FileTextIcon"
                size="18"
              />
            </span>
            <div>
              <h3 class="sc-card__title">
                Certificates
                <span class="sc-card__count">{{ certificateRows.length }}</span>
              </h3>
              <p class="sc-card__subtitle">
                Certificates &amp; related requests for this contact — including
                applications still pending or cancelled, so a client with no
                certificate yet isn't shown as if he never applied.
              </p>
            </div>
          </header>

          <div class="sc-card__body">
            <div
              v-if="!certificateRows.length"
              class="sc-empty"
            >
              <div class="sc-empty__icon">
                <feather-icon
                  icon="FileMinusIcon"
                  size="26"
                />
              </div>
              <p class="mb-0 text-muted">
                No certificates or applications found for this contact.
              </p>
            </div>

            <div
              v-else
              class="sc-cert-grid"
            >
              <article
                v-for="certificate in certificateRows"
                :key="certificate.key"
                class="sc-cert"
                :class="{ 'sc-cert--pending': certificate.placeholder }"
              >
                <!--
                  crm_client_certificates is a live VIEW over the ERP (since 2 Sep) —
                  a FLAT row, not a mirror with nested .request/.certificate objects.
                  This card read the old nested shape and silently failed to render
                  anything (Karim, 2 Sep: "certificates data is not appearing,
                  appearing only the request code"). certificate_name is the PRODUCT
                  — the thing actually sold — and it was never shown at all.
                  status / payment_status / final_price / process_type do not exist
                  on the view and are gone; they were never real fields the mirror
                  had either, just assumed.

                  A `placeholder` row is a request that hasn't produced a real
                  certificate yet (open, pending, or cancelled) — see the
                  certificateRows computed.
                -->
                <div class="sc-cert__head">
                  <h4 class="sc-cert__title">
                    <feather-icon
                      :icon="certificate.placeholder ? 'ClockIcon' : 'AwardIcon'"
                      size="15"
                    />
                    {{ certificate.certificate_name || 'Certificate' }}
                    <span
                      v-if="certificate.placeholder"
                      class="sc-pill sc-pill--open ml-1"
                    >{{ certificate.status || 'in progress' }}</span>
                  </h4>
                </div>

                <div
                  v-if="!certificate.placeholder"
                  class="sc-cert__dates"
                >
                  <span class="sc-chip sc-chip--success">
                    <feather-icon
                      icon="CalendarIcon"
                      size="12"
                    />
                    Issued: {{ formatDate(certificate.issued_at) }}
                  </span>
                  <span class="sc-chip sc-chip--danger">
                    <feather-icon
                      icon="ClockIcon"
                      size="12"
                    />
                    Expires: {{ formatDate(certificate.expired_at) }}
                  </span>
                </div>

                <!-- Certificate info -->
                <div class="sc-cert__section">
                  <span class="sc-cert__section-title">Certificate Info</span>
                  <ul class="sc-detail">
                    <li><span>Number</span><strong>{{ certificate.certificate_number || '— (pending)' }}</strong></li>
                    <li
                      v-if="certificate.sub_certificate_name"
                      class="sc-detail__sector"
                    >
                      <span>Sector</span>
                      <strong>
                        {{ certificate.sub_certificate_name.en || certificate.sub_certificate_name }}
                        <template v-if="certificate.sub_certificate_name.ar">
                          <br>{{ certificate.sub_certificate_name.ar }}
                        </template>
                      </strong>
                    </li>
                  </ul>
                </div>

                <!--
                  Karim, 2 Sep: "any request number must be seen with certificate
                  number and client name and facility together, so we can know as
                  human sales." Every row — real certificate or pending placeholder
                  — carries all four.
                -->
                <div class="sc-cert__section">
                  <span class="sc-cert__section-title">Request</span>
                  <ul class="sc-detail">
                    <li v-if="certificate.request_code">
                      <span>Code</span><strong>{{ certificate.request_code }}</strong>
                    </li>
                    <li><span>Client</span><strong>{{ delegator.name || '—' }}</strong></li>
                    <li><span>Facility</span><strong>{{ certificate.facilityName || '—' }}</strong></li>
                    <li v-if="certificate.source">
                      <span>Source</span><strong>{{ certificate.source }}</strong>
                    </li>
                  </ul>
                </div>
              </article>
            </div>
          </div>
        </section>
      </div>
    </template>

    <!-- New Opportunity modal -->
    <div
      v-if="showOppModal"
      class="sc-modal"
      @click.self="showOppModal = false"
    >
      <div class="sc-modal__box">
        <header class="sc-modal__head">
          <h4 class="mb-0">
            New opportunity
          </h4>
          <button
            type="button"
            class="sc-modal__close"
            @click="showOppModal = false"
          >
            <feather-icon
              icon="XIcon"
              size="18"
            />
          </button>
        </header>
        <div class="sc-modal__body">
          <p
            class="text-muted mb-1"
            style="font-size:.82rem"
          >
            Creates a CRM opportunity for <strong>{{ delegator.name }}</strong>.
          </p>
          <div class="form-group">
            <label>Service / scheme</label>
            <select
              v-model="opp.serviceScheme"
              class="form-control"
            >
              <option :value="null">
                — select —
              </option>
              <option
                v-for="s in schemeOptions"
                :key="s"
                :value="s"
              >
                {{ s }}
              </option>
            </select>
          </div>
          <div class="form-row">
            <div class="form-group col-6">
              <label>Type</label>
              <select
                v-model="opp.type"
                class="form-control"
              >
                <option value="new">
                  New
                </option>
                <option value="renewal">
                  Renewal
                </option>
                <option value="extend">
                  Extend
                </option>
                <option value="survey">
                  Survey
                </option>
              </select>
            </div>
            <div class="form-group col-6">
              <label>Estimated value</label>
              <input
                v-model.number="opp.value"
                type="number"
                min="0"
                class="form-control"
              >
            </div>
          </div>
          <div class="form-group">
            <label>Expected close date</label>
            <input
              v-model="opp.expectedCloseDate"
              type="date"
              class="form-control"
            >
          </div>
          <!-- Karim, 3 Sep: "when creating opportunity you should have the
               list." Manager/admin only — left blank, the backend keeps the
               client's existing owner (or you if unowned). -->
          <div
            v-if="access.canViewTeam"
            class="form-group"
          >
            <label>Assign to</label>
            <select
              v-model="opp.ownerId"
              class="form-control"
            >
              <option value="">
                Myself / keep current owner
              </option>
              <option
                v-for="member in team"
                :key="member.id"
                :value="String(member.id)"
              >
                {{ memberLabel(member) }}
              </option>
            </select>
          </div>
        </div>
        <footer class="sc-modal__foot">
          <button
            type="button"
            class="sc-btn sc-btn--ghost"
            @click="showOppModal = false"
          >
            Cancel
          </button>
          <button
            type="button"
            class="sc-btn sc-btn--success"
            :disabled="!opp.serviceScheme || creatingOpp"
            @click="createOpportunity"
          >
            <feather-icon
              icon="TargetIcon"
              size="15"
            />
            {{ creatingOpp ? 'Creating…' : 'Create' }}
          </button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import decryptData from '@/store/auth/decryptData'

const AVATAR_COLORS = [
  'linear-gradient(135deg, #7367f0, #9e95f5)',
  'linear-gradient(135deg, #00cfe8, #5be0ef)',
  'linear-gradient(135deg, #28c76f, #48da89)',
  'linear-gradient(135deg, #ff9f43, #ffb976)',
  'linear-gradient(135deg, #ea5455, #f08182)',
  'linear-gradient(135deg, #5a8dee, #7aa5f2)',
]

export default {
  name: 'ShowContactCRM',
  data() {
    return {
      loadingData: false,
      delegator: {},
      savingTier: false,
      /**
       * What is actually happening with this client, read live from the ERP.
       * Karim, 2 Sep: the facilities he acts for, his requests (which is all a
       * client WITHOUT a certificate has), and the nearest certificate to expire.
       */
      crm: {
        facilities: [],
        requests: [],
        requestSummary: {},
        nearestCertificate: null,
        certificates: [],
        // Karim, 3 Sep: "if I opened a client, I can see the open
        // opportunities for it... and I can open multiple opportunities."
        cards: [],
      },
      showOppModal: false,
      creatingOpp: false,
      schemeOptions: [],
      opp: {
        serviceScheme: null,
        type: 'new',
        value: 0,
        expectedCloseDate: '',
        ownerId: '',
      },
      // Karim, 3 Sep: "when creating opportunity you should have the list."
      access: { canViewTeam: false },
      team: [],
    }
  },
  computed: {
    /**
     * Karim, 2 Sep: "the clients with no certificates should at least have
     * cancelled or pending certificates still going, then the certificate number
     * can be empty and filled out later." Merges the real certificates with a
     * placeholder row for every request that has NOT (yet) produced one — matched
     * by request_code. A client who only ever applied and never got issued a
     * certificate used to show an empty Certificates card; now their open/pending/
     * cancelled applications live there too, with certificate_number left blank.
     */
    certificateRows() {
      const facilityNames = {}
      const facilities = this.crm.facilities || []
      facilities.forEach(f => { facilityNames[f.facilityId] = f.name })

      const real = (this.crm.certificates || []).map(c => ({
        key: `cert-${c.erp_certificate_id || c.id || c.request_code}`,
        placeholder: false,
        certificate_number: c.certificate_number,
        certificate_name: c.certificate_name,
        sub_certificate_name: c.sub_certificate_name,
        issued_at: c.issued_at,
        expired_at: c.expired_at,
        request_code: c.request_code,
        source: c.source,
        facilityName: facilityNames[c.erp_facility_id] || null,
        status: null,
      }))

      const covered = new Set(real.map(c => c.request_code).filter(Boolean))

      const placeholders = (this.crm.requests || [])
        .filter(r => r.code && !covered.has(r.code))
        .map(r => ({
          key: `req-${r.id}`,
          placeholder: true,
          certificate_number: null,
          certificate_name: r.certificate,
          sub_certificate_name: null,
          issued_at: null,
          expired_at: null,
          request_code: r.code,
          source: r.source,
          facilityName: r.facilityName || facilityNames[r.facilityId] || null,
          status: r.status,
        }))

      return [...real, ...placeholders]
    },
    avatarColor() {
      const id = Number(this.$route.params.id) || (this.delegator.name ? this.delegator.name.length : 0)
      return AVATAR_COLORS[id % AVATAR_COLORS.length]
    },
    isBroker() {
      return [true, 1, '1', 'true'].includes(this.delegator.is_broker)
    },
  },
  mounted() {
    this.showDelegator()
    this.fetchSchemes()
    this.fetchAccess()
  },
  methods: {
    async fetchAccess() {
      try {
        const res = await axios.get('crm/me')
        const body = decryptData(res.data.payload)
        if (body && body.data) this.access = body.data
        // Karim, 3 Sep: "when creating opportunity you should have the list."
        if (this.access.canViewTeam) this.fetchTeam()
      } catch (e) { /* optional */ }
    },
    async fetchTeam() {
      try {
        const res = await axios.get('crm/team')
        const body = decryptData(res.data.payload)
        this.team = (body && body.data) || []
      } catch (e) { /* optional */ }
    },
    memberLabel(member) {
      if (!member) return ''
      const roles = Array.isArray(member.roles) && member.roles.length ? member.roles.join('/') : ''
      const office = member.officeName || 'No office'
      return roles ? `${member.name} (${roles}) — ${office}` : `${member.name} — ${office}`
    },
    formatDate(value) {
      if (!value) return '—'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) {
        return typeof value === 'string' ? value.split('T')[0] : value
      }
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    formatNumber(value) {
      const n = Number(value)
      return Number.isFinite(n) ? n.toLocaleString('en-US') : (value || 0)
    },
    pipelineLabel(pipeline) {
      const map = { deals: 'New Deal', renewals: 'Renewal', tickets: 'Ticket' }
      return map[pipeline] || pipeline || '—'
    },
    initials(name) {
      if (!name) return '??'
      return name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map(part => part.charAt(0).toUpperCase())
        .join('')
    },
    goBack() {
      this.$router.push({ name: 'crm-system-contacts' }).catch(() => {})
    },
    async fetchSchemes() {
      try {
        const res = await axios.get('crm/services')
        const body = decryptData(res.data.payload)
        const schemes = body && body.data && body.data.schemes
        if (Array.isArray(schemes)) {
          this.schemeOptions = schemes.map(s => s.scheme).filter(Boolean)
        }
      } catch (e) { /* optional */ }
    },
    async createOpportunity() {
      this.creatingOpp = true
      try {
        // Karim, 3 Sep: only sent when a manager actually picked someone — the
        // backend (CrmClientController::createOpportunity) already defaults to
        // the client's existing owner (or you) when this is absent, and ignores
        // it entirely for a rep. `owner` (the name) must travel WITH ownerId —
        // the backend falls back to the client's OLD owner name otherwise, which
        // would show the wrong name next to the newly picked owner's id.
        const owner = this.opp.ownerId ? this.team.find(u => String(u.id) === String(this.opp.ownerId)) : null
        await axios.post(`crm/clients/${this.$route.params.id}/opportunity`, {
          serviceScheme: this.opp.serviceScheme,
          type: this.opp.type,
          value: this.opp.value,
          expectedCloseDate: this.opp.expectedCloseDate || null,
          ...(owner ? { owner: owner.name, ownerId: owner.id } : {}),
        })
        this.showOppModal = false
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Opportunity created',
            text: `${this.opp.serviceScheme} — see the Opportunities board`,
            variant: 'success',
            icon: 'CheckIcon',
          },
        })
        this.opp = {
          serviceScheme: null, type: 'new', value: 0, expectedCloseDate: '', ownerId: '',
        }
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Failed',
            text: 'Could not create the opportunity',
            variant: 'danger',
            icon: 'AlertCircleIcon',
          },
        })
      } finally {
        this.creatingOpp = false
      }
    },
    goEdit() {
      this.$router.push({ name: 'crm-system-contact-edit', params: { id: this.$route.params.id } }).catch(() => {})
    },
    async showDelegator() {
      if (!this.$route.params.id) return
      this.loadingData = true
      try {
        const res = await axios.get(`/delegator/signed/${this.$route.params.id}`)
        this.delegator = decryptData(res.data.payload).data.delegator
      } finally {
        this.loadingData = false
      }

      // Fetched separately and never allowed to break the page: if this fails the
      // contact's own details are still worth showing.
      this.loadCrmActivity()
    },

    goToCompany(facilityId) {
      this.$router.push({ name: 'crm-system-company-show', params: { id: facilityId } })
    },

    async loadCrmActivity() {
      try {
        const res = await axios.get(`crm/clients/${this.$route.params.id}`)
        const d = decryptData(res.data.payload).data || {}
        this.crm = {
          facilities: d.facilities || [],
          requests: d.requests || [],
          requestSummary: d.requestSummary || {},
          nearestCertificate: d.nearestCertificate || null,
          // The flat crm_client_certificates view (erp_certificate_id,
          // certificate_number, certificate_name, sub_certificate_name, issued_at,
          // expired_at, request_code, source) — NOT the legacy nested
          // delegator.certificates shape from /delegator/signed/{id}, which this
          // page used to read and which the template's flat field names never
          // matched (Karim, 2 Sep: "certificates data is not appearing").
          certificates: d.certificates || [],
          // Karim, 3 Sep: showClient() already constrains this to OPEN cards
          // only (not won/rejected/renewed/dismissed) — see
          // CrmClientController::CLOSED_CARD_STAGES.
          cards: d.cards || [],
        }
      } catch (e) {
        // Leave the empty shape; the sections below simply do not render.
      }
    },
    /** Karim, 2 Sep: "add client rating" — gold / silver / bronze, or cleared. */
    async setTier(value) {
      const tier = value || null
      this.savingTier = true
      try {
        await axios.post(`crm/clients/${this.$route.params.id}/tier`, { tier })
        this.delegator = { ...this.delegator, tier }
      } catch (e) {
        // Leave delegator.tier untouched — the select reverts to it on re-render.
      } finally {
        this.savingTier = false
      }
    },
  },
}
</script>

<style lang="scss" src="./show-contact-crm.scss" scoped></style>
